import React, { useState, useEffect, useCallback, useMemo } from "react";
import { get, post, put } from "../utils/api";
import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  Form,
  InputGroup,
  Table,
  Spinner,
  Badge,
} from "react-bootstrap";
import ProgressDialog from "./ProgressDialog";
import { 
  FaEnvelope, FaPhoneAlt, FaClock, FaUserShield, FaPlus, 
  FaSearch, FaSyncAlt, FaClipboard, FaCheckCircle, FaTimesCircle, 
  FaUserMd, FaCrown, FaTruck, FaUser, FaThLarge, FaList, FaTimes, 
  FaKey, FaToggleOn, FaToggleOff, FaShieldAlt
} from "react-icons/fa";
import { useToast } from "./ToastContext";
import { getRoles } from "../utils/roles";
import NoDataCard from "./NoDataCard";
import "./UserManagement.css";

const formatLastLogin = (lastLoginDate) => {
  if (!lastLoginDate) return "Never";
  const date = new Date(lastLoginDate);
  if (isNaN(date.getTime())) return "N/A";

  const day = date.getDate().toString().padStart(2, '0');
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  const formattedDate = `${hours}:${minutes} ${ampm}, ${day} ${month} ${year}`;
  const seconds = Math.floor((new Date() - date) / 1000);

  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;
  return formattedDate;
};

const getRoleIcon = (role) => {
  switch ((role || '').toLowerCase()) {
    case 'admin': return <FaCrown className="role-icon-inline text-warning" />;
    case 'pharmacist': return <FaUserMd className="role-icon-inline text-info" />;
    case 'delivery': return <FaTruck className="role-icon-inline text-primary" />;
    default: return <FaUser className="role-icon-inline text-success" />;
  }
};

const UserManagement = () => {
  const { showToast } = useToast();
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters & View
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid"); // 'grid' | 'table'
  const [roles, setRoles] = useState([]);

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [savingRole, setSavingRole] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingPharmacist, setCreatingPharmacist] = useState(false);
  const [newPharmacist, setNewPharmacist] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = roleFilter ? `/users?role=${roleFilter}&limit=1000` : `/users?limit=1000`;
      const { data } = await get(endpoint);
      setUserList(data || []);
    } catch (err) {
      setError(err.message || "Failed to fetch users.");
      setUserList([]);
    } finally {
      setLoading(false);
    }
  }, [roleFilter]);

  const fetchRoles = async () => {
    try {
      const { data } = await getRoles();
      setRoles(data || []);
    } catch (err) {
      console.error("Failed to load roles", err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [fetchUsers]);

  const handleChangeStatus = async (userId, currentStatus) => {
    try {
      await post(`/users/${userId}/status`, {
        is_active: currentStatus ? 0 : 1,
      });
      fetchUsers();
      showToast(`User ${currentStatus ? 'deactivated' : 'activated'} successfully!`, "success");
    } catch (err) {
      showToast(`Failed to update status: ${err.message}`, "danger");
    }
  };

  const handleShowModal = (user) => {
    setSelectedUser(user);
    setSelectedRole(user.role);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setSelectedRole("");
  };

  const handleAssignRole = async () => {
    setSavingRole(true);
    try {
      await put(`/users/${selectedUser.user_id}/role`, { role: selectedRole });
      handleCloseModal();
      fetchUsers();
      showToast("Role assigned successfully!", "success");
    } catch (err) {
      showToast(`Failed to assign role: ${err.message}`, "danger");
    } finally {
      setSavingRole(false);
    }
  };

  const handleCreatePharmacist = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!newPharmacist.name?.trim()) errors.name = "Full name is required";
    if (!newPharmacist.email?.trim()) errors.email = "Email address is required";
    if (!newPharmacist.mobile?.trim()) errors.mobile = "Mobile number is required";
    if (!newPharmacist.password || newPharmacist.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    setCreatingPharmacist(true);
    try {
      const response = await post("/users/pharmacist", newPharmacist);
      if (response && response.success) {
        setShowCreateModal(false);
        setNewPharmacist({ name: "", email: "", mobile: "", password: "" });
        fetchUsers();
        showToast("Pharmacist user registered successfully!", "success");
      } else {
        showToast(response?.message || "Failed to create pharmacist", "danger");
      }
    } catch (err) {
      showToast(`Failed to create pharmacist: ${err.message}`, "danger");
    } finally {
      setCreatingPharmacist(false);
    }
  };

  const handleCopy = async (text, label = "Value") => {
    try {
      if (!navigator?.clipboard) throw new Error("Clipboard not available");
      await navigator.clipboard.writeText(text || "");
      showToast(`${label} copied to clipboard`, "success");
    } catch {
      showToast(`Failed to copy ${label}`, "danger");
    }
  };

  // KPIs
  const metrics = useMemo(() => {
    const total = userList.length;
    const active = userList.filter(u => u.is_active !== 0 && u.is_active !== false).length;
    const pharmacists = userList.filter(u => (u.role || '').toLowerCase() === 'pharmacist').length;
    const admins = userList.filter(u => (u.role || '').toLowerCase() === 'admin').length;
    return { total, active, pharmacists, admins };
  }, [userList]);

  // Filtered List
  const filteredUsers = useMemo(() => {
    return userList.filter(u => {
      if (statusFilter === 'active' && (u.is_active === 0 || u.is_active === false)) return false;
      if (statusFilter === 'inactive' && (u.is_active !== 0 && u.is_active !== false)) return false;
      if (searchTerm.trim() !== '') {
        const q = searchTerm.toLowerCase();
        const matchName = (u.name || '').toLowerCase().includes(q);
        const matchEmail = (u.email || '').toLowerCase().includes(q);
        const matchPhone = (u.mobile || '').toLowerCase().includes(q);
        const matchId = String(u.user_id || '').includes(q);
        if (!matchName && !matchEmail && !matchPhone && !matchId) return false;
      }
      return true;
    });
  }, [userList, statusFilter, searchTerm]);

  return (
    <Container fluid className="user-mgmt-page px-3 px-lg-4 py-3">
      <ProgressDialog show={loading && userList.length === 0} message="Loading user directory..." />

      {/* Header & KPI Summary */}
      <div className="usr-header-panel mb-4 p-3 p-lg-4 rounded glassmorphic-panel">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
          <div className="d-flex align-items-center gap-3">
            <div className="usr-master-icon">
              <FaUserShield />
            </div>
            <div>
              <h3 className="usr-title mb-0">Staff & User Directory</h3>
              <p className="usr-subtitle mb-0 text-muted">Manage system administrators, licensed pharmacists, delivery agents and user roles.</p>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Button 
              variant="primary" 
              size="sm" 
              onClick={() => { setFormErrors({}); setShowCreateModal(true); }}
              className="usr-add-btn d-flex align-items-center gap-2"
            >
              <FaPlus /> Create Pharmacist
            </Button>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={fetchUsers} 
              className="usr-refresh-btn d-flex align-items-center gap-2"
            >
              <FaSyncAlt className={loading ? 'fa-spin' : ''} /> Refresh
            </Button>
          </div>
        </div>

        {/* KPI Metrics */}
        <Row className="g-3">
          <Col xs={6} md={3}>
            <div className="usr-kpi-card">
              <div className="kpi-icon total">
                <FaUser />
              </div>
              <div className="kpi-content">
                <div className="kpi-value">{metrics.total}</div>
                <div className="kpi-label">Total Users</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="usr-kpi-card">
              <div className="kpi-icon active">
                <FaCheckCircle />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-success">{metrics.active}</div>
                <div className="kpi-label">Active Accounts</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="usr-kpi-card">
              <div className="kpi-icon pharmacist">
                <FaUserMd />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-info">{metrics.pharmacists}</div>
                <div className="kpi-label">Pharmacists</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="usr-kpi-card">
              <div className="kpi-icon admin">
                <FaCrown />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-warning">{metrics.admins}</div>
                <div className="kpi-label">Admins</div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Toolbar & Filter Bar */}
      <div className="usr-toolbar-panel mb-4 p-3 rounded glassmorphic-panel">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          {/* Search Box */}
          <div className="usr-search-wrapper flex-grow-1">
            <InputGroup className="usr-search-group">
              <InputGroup.Text className="bg-transparent border-0 pe-1 text-muted">
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search staff by name, email, mobile, user ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="usr-search-input border-0 ps-1"
              />
              {searchTerm && (
                <Button 
                  variant="link" 
                  onClick={() => setSearchTerm('')} 
                  className="usr-clear-search text-muted border-0"
                >
                  <FaTimes />
                </Button>
              )}
            </InputGroup>
          </div>

          {/* Role Filter Selector */}
          <div className="d-flex align-items-center gap-2">
            <Form.Select 
              value={roleFilter} 
              onChange={(e) => setRoleFilter(e.target.value)}
              className="usr-select-pill"
              size="sm"
            >
              <option value="">All Roles</option>
              {roles.map((r) => (
                <option key={r.role_id} value={r.name}>
                  {r.name.toUpperCase()}
                </option>
              ))}
            </Form.Select>
          </div>

          {/* Status Filter Chips */}
          <div className="usr-status-chips d-flex align-items-center gap-2 flex-wrap">
            <button 
              type="button" 
              className={`usr-filter-chip ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All ({metrics.total})
            </button>
            <button 
              type="button" 
              className={`usr-filter-chip active-chip ${statusFilter === 'active' ? 'active' : ''}`}
              onClick={() => setStatusFilter('active')}
            >
              Active ({metrics.active})
            </button>
            <button 
              type="button" 
              className={`usr-filter-chip inactive ${statusFilter === 'inactive' ? 'active' : ''}`}
              onClick={() => setStatusFilter('inactive')}
            >
              Disabled ({metrics.total - metrics.active})
            </button>
          </div>

          {/* View Mode Toggle */}
          <div className="usr-view-switch">
            <button 
              type="button" 
              className={`switch-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <FaThLarge />
            </button>
            <button 
              type="button" 
              className={`switch-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              <FaList />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {filteredUsers.length === 0 && !loading ? (
        <div className="usr-no-data-box py-5 text-center rounded glassmorphic-panel">
          <NoDataCard 
            title="No Users Found"
            message={searchTerm || roleFilter ? "No user accounts match your search or filter criteria." : "There are no users found in this directory."}
            icon="👥"
            actionButton={
              (searchTerm || roleFilter || statusFilter !== 'all') ? (
                <Button variant="primary" size="sm" onClick={() => { setSearchTerm(''); setRoleFilter(''); setStatusFilter('all'); }}>
                  Clear Filters
                </Button>
              ) : (
                <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                  Create Pharmacist
                </Button>
              )
            }
          />
        </div>
      ) : viewMode === 'grid' ? (
        <div className="usr-cards-grid">
          {filteredUsers.map((user) => {
            const initial = (user.name || 'U').charAt(0).toUpperCase();
            const isActive = user.is_active !== 0 && user.is_active !== false;

            return (
              <div className="usr-card-item glassmorphic-panel" key={user.user_id}>
                <div className="usr-card-header p-3 border-bottom d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-3">
                    <div className={`usr-avatar-circle role-${(user.role || '').toLowerCase()}`}>
                      {initial}
                    </div>
                    <div>
                      <h6 className="usr-card-name mb-0">{user.name}</h6>
                      <div className="d-flex align-items-center gap-2 mt-1">
                        <span className={`usr-role-badge role-${(user.role || '').toLowerCase()}`}>
                          {getRoleIcon(user.role)} {user.role?.toUpperCase()}
                        </span>
                        <span className="usr-id-pill">#{user.user_id}</span>
                      </div>
                    </div>
                  </div>

                  <button 
                    type="button" 
                    className={`usr-status-toggle ${isActive ? 'active' : 'inactive'}`}
                    onClick={() => handleChangeStatus(user.user_id, isActive)}
                    title={`Click to ${isActive ? 'Deactivate' : 'Activate'} user`}
                  >
                    {isActive ? <><FaToggleOn className="me-1" /> Active</> : <><FaToggleOff className="me-1" /> Disabled</>}
                  </button>
                </div>

                <div className="usr-card-body p-3">
                  {/* Email */}
                  <div className="usr-contact-item d-flex justify-content-between align-items-center mb-2">
                    <span className="usr-contact-label">
                      <FaEnvelope className="me-2 text-primary small" /> Email
                    </span>
                    <div className="d-flex align-items-center gap-1">
                      <span className="usr-contact-val text-truncate" style={{ maxWidth: '170px' }} title={user.email || 'N/A'}>
                        {user.email || 'N/A'}
                      </span>
                      {user.email && (
                        <button 
                          type="button" 
                          className="usr-copy-btn" 
                          onClick={() => handleCopy(user.email, 'Email address')}
                          title="Copy email"
                        >
                          <FaClipboard />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="usr-contact-item d-flex justify-content-between align-items-center mb-2">
                    <span className="usr-contact-label">
                      <FaPhoneAlt className="me-2 text-info small" /> Mobile
                    </span>
                    <div className="d-flex align-items-center gap-1">
                      <span className="usr-contact-val text-white">{user.mobile || 'N/A'}</span>
                      {user.mobile && (
                        <button 
                          type="button" 
                          className="usr-copy-btn" 
                          onClick={() => handleCopy(user.mobile, 'Mobile number')}
                          title="Copy mobile"
                        >
                          <FaClipboard />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Last Login */}
                  <div className="usr-meta-row p-2 rounded small mt-2 d-flex justify-content-between align-items-center">
                    <span className="text-muted"><FaClock className="me-1" /> Last Login:</span>
                    <span className="text-white fw-semibold">{formatLastLogin(user.last_login)}</span>
                  </div>
                </div>

                <div className="usr-card-footer p-2 border-top d-flex gap-2">
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="w-100 usr-action-btn"
                    onClick={() => handleShowModal(user)}
                  >
                    <FaUserShield className="me-1" /> Change Role
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="usr-table-wrapper rounded glassmorphic-panel overflow-hidden">
          <Table responsive hover className="usr-data-table mb-0 align-middle">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>ID</th>
                <th>Staff Name</th>
                <th>Assigned Role</th>
                <th>Email Contact</th>
                <th>Mobile</th>
                <th>Last Login</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const isActive = u.is_active !== 0 && u.is_active !== false;

                return (
                  <tr key={u.user_id}>
                    <td className="text-muted fw-bold">#{u.user_id}</td>
                    <td>
                      <div className="fw-bold text-white d-flex align-items-center gap-2">
                        <div className={`usr-mini-avatar role-${(u.role || '').toLowerCase()}`}>
                          {(u.name || 'U').charAt(0).toUpperCase()}
                        </div>
                        {u.name}
                      </div>
                    </td>
                    <td>
                      <span className={`usr-role-badge role-${(u.role || '').toLowerCase()}`}>
                        {getRoleIcon(u.role)} {u.role?.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className="text-muted">{u.email || 'N/A'}</span>
                    </td>
                    <td>
                      <span className="text-white">{u.mobile || 'N/A'}</span>
                    </td>
                    <td>
                      <span className="small text-muted">{formatLastLogin(u.last_login)}</span>
                    </td>
                    <td>
                      <button 
                        type="button" 
                        className={`usr-status-toggle ${isActive ? 'active' : 'inactive'}`}
                        onClick={() => handleChangeStatus(u.user_id, isActive)}
                      >
                        {isActive ? '● Active' : '● Disabled'}
                      </button>
                    </td>
                    <td className="text-end">
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="usr-tbl-btn"
                        onClick={() => handleShowModal(u)}
                        title="Change Role"
                      >
                        <FaUserShield /> Role
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {/* Assign Role Modal */}
      <Modal show={showModal} onHide={handleCloseModal} scrollable className="usr-modal-modern">
        <Modal.Header closeButton className="usr-modal-header py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="usr-modal-icon">
              <FaUserShield />
            </div>
            <div>
              <Modal.Title className="usr-modal-title mb-0">Assign User Role</Modal.Title>
              <div className="usr-modal-subtitle">
                User <span className="fw-bold text-primary">{selectedUser?.name}</span> (#{selectedUser?.user_id})
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="usr-modal-body p-4">
          {selectedUser && (
            <div>
              <div className="usr-summary-box p-3 rounded mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="usr-summary-label">Current Role:</span>
                  <span className={`usr-role-badge role-${(selectedUser.role || '').toLowerCase()}`}>
                    {getRoleIcon(selectedUser.role)} {selectedUser.role?.toUpperCase()}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="usr-summary-label">Account Email:</span>
                  <span className="usr-summary-value">{selectedUser.email}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="usr-summary-label">Account Status:</span>
                  <span className={selectedUser.is_active ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                    {selectedUser.is_active ? '● Active' : '● Disabled'}
                  </span>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="usr-modal-label">Select Target Role</Form.Label>
                <Form.Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="usr-modal-select"
                >
                  {roles.map((role) => (
                    <option key={role.role_id} value={role.name}>
                      {role.name.toUpperCase()}
                    </option>
                  ))}
                </Form.Select>
                <div className="usr-modal-hint mt-2">
                  Assigning a new role grants or revokes page and feature permissions according to the Role Matrix.
                </div>
              </Form.Group>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="usr-modal-footer d-flex justify-content-between py-3 px-4">
          <Button variant="secondary" onClick={handleCloseModal} disabled={savingRole}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAssignRole} disabled={savingRole} className="usr-submit-btn fw-bold px-4">
            {savingRole ? <><Spinner animation="border" size="sm" className="me-1" /> Saving...</> : 'Save Role Assignment'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Register Pharmacist Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} scrollable className="usr-modal-modern">
        <Modal.Header closeButton className="usr-modal-header py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="usr-modal-icon">
              <FaUserMd />
            </div>
            <div>
              <Modal.Title className="usr-modal-title mb-0">Register New Pharmacist</Modal.Title>
              <div className="usr-modal-subtitle">Create verified pharmacist administrative login</div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="usr-modal-body p-4">
          <Form onSubmit={handleCreatePharmacist}>
            <Form.Group className="mb-3">
              <Form.Label className="usr-modal-label">Full Legal Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Dr. Jane Smith"
                value={newPharmacist.name}
                onChange={(e) => {
                  setNewPharmacist({ ...newPharmacist, name: e.target.value });
                  if (formErrors.name) setFormErrors({ ...formErrors, name: null });
                }}
                className={`usr-modal-input ${formErrors.name ? 'is-invalid' : ''}`}
              />
              {formErrors.name && <div className="text-danger small mt-1">{formErrors.name}</div>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="usr-modal-label">Email Address *</Form.Label>
              <Form.Control
                type="email"
                placeholder="e.g. jane.pharmacist@medstore.com"
                value={newPharmacist.email}
                onChange={(e) => {
                  setNewPharmacist({ ...newPharmacist, email: e.target.value });
                  if (formErrors.email) setFormErrors({ ...formErrors, email: null });
                }}
                className={`usr-modal-input ${formErrors.email ? 'is-invalid' : ''}`}
              />
              {formErrors.email && <div className="text-danger small mt-1">{formErrors.email}</div>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="usr-modal-label">Mobile Contact *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. 9876543210"
                value={newPharmacist.mobile}
                onChange={(e) => {
                  setNewPharmacist({ ...newPharmacist, mobile: e.target.value });
                  if (formErrors.mobile) setFormErrors({ ...formErrors, mobile: null });
                }}
                className={`usr-modal-input ${formErrors.mobile ? 'is-invalid' : ''}`}
              />
              {formErrors.mobile && <div className="text-danger small mt-1">{formErrors.mobile}</div>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="usr-modal-label">Password * (Min 6 chars)</Form.Label>
              <Form.Control
                type="password"
                placeholder="••••••••"
                value={newPharmacist.password}
                onChange={(e) => {
                  setNewPharmacist({ ...newPharmacist, password: e.target.value });
                  if (formErrors.password) setFormErrors({ ...formErrors, password: null });
                }}
                className={`usr-modal-input ${formErrors.password ? 'is-invalid' : ''}`}
              />
              {formErrors.password && <div className="text-danger small mt-1">{formErrors.password}</div>}
            </Form.Group>

            <div className="d-flex justify-content-end pt-3 border-top">
              <Button variant="primary" type="submit" disabled={creatingPharmacist} className="usr-submit-btn fw-bold px-4">
                {creatingPharmacist ? <><Spinner animation="border" size="sm" className="me-1" /> Registering...</> : 'Register Pharmacist'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default UserManagement;